import {Unit} from '@benchling/units';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {
  densityUnitTypePredicate,
  massUnitTypePredicate,
  molarityUnitTypePredicate,
  volumeUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import {resolveUnitsFromUnitIds} from 'notebook/tables/string-resolution/UnitsResolution';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolvedValueInfo, ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {UnitReferenceTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

export const DEFAULT_MOL_UNIT_SYMBOL = 'mol';
export const DEFAULT_MASS_UNIT_SYMBOL = 'g';
export const DEFAULT_VOLUME_UNIT_SYMBOL = 'L';
export const DEFAULT_DENSITY_UNIT_SYMBOL = 'g/L';
export const DEFAULT_MOLARITY_UNIT_SYMBOL = 'M';

// Chemistry equations use g as the base unit for mass instead of kg, so we often need to convert from kg to g.
export const KG_TO_G_CONVERSION_FACTOR = 1000;

// For stoichiometry amount calculations, we can use either mass or volume units.
export function stoichiometryAmountUnitTypePredicate(unitType: Unit['unitType']): boolean {
  return massUnitTypePredicate(unitType) || volumeUnitTypePredicate(unitType);
}

// For stoichiometry concentration calculations, we can use either density or molarity units.
export function stoichiometryConcentrationUnitTypePredicate(unitType: Unit['unitType']): boolean {
  return densityUnitTypePredicate(unitType) || molarityUnitTypePredicate(unitType);
}

/**
 * Get the factor (from GraphQL cache) to convert a unit reference to another unit.
 *
 * IMPORTANT: GraphQL stores conversion factors relative to SI base units (kg is BaseUnit for mass),
 * but chemistry calculations use g/mol for molecular weight. We need to convert from
 * the unit's SI base (kg) to chemistry standard (g).
 *
 * @param sourceUnitTypedValue - The unit typed value to convert
 * @param targetUnitSymbol - The symbol of the measurement unit to convert to
 * @returns The conversion factor as a PreciseDecimal, null if unit cannot be determined, or if the unit
 * is not yet in the cache (triggers a background fetch for subsequent calls).
 */
export function getStandardChemistryUnitConversionFactor(
  unit: MeasurementUnit | undefined,
  additionalConversionFactor: number = 1
): PreciseDecimal | undefined {
  if (!unit) {
    return undefined;
  }
  const conversionFactorToSIBase = PreciseDecimal.of(unit.conversionFactor);
  return PreciseDecimal.of(conversionFactorToSIBase.decimal.mul(additionalConversionFactor));
}

/** Given a bunch of TypedValues, resolve any Units from them, loading them as Measurement units. */
export function resolveUnitsFromTypedValues(
  typedValues: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): {
  units?: Array<MeasurementUnit>;
  error?: ResolvedValueInfo['error'];
} {
  const unitIds: Array<string> = typedValues
    .filter(
      (typedValue): typedValue is UnitReferenceTypedValue =>
        typedValue != null && typedValue.type === BaseTypedValueType.UNIT_REFERENCE
    )
    .map((unitTypedValue) => unitTypedValue.unitSummary.id);

  return resolveUnitsFromUnitIds(unitIds, resolveFromResolutionQuery);
}
